import { format } from "date-fns";
import { useState } from "react";
import { Icon, Typography, DropdownMenu } from "@/components/atoms";
import { DataTable, DataTableColumnDef } from "@/components/molecules";
import { useProfile } from "@/hooks";
import { useAppDispatch } from "@/redux/hooks";
import { setPaymentAction } from "@/redux/slices/booking/bookingSlice";
import { BookingPaymentLog } from "@/redux/slices/booking/types";
import { formatCurrency } from "@/utils";
import { useSelectedBooking } from "../../../hooks/useSelectedBooking";
import { PaymentStatus } from "../PaymentStatus";
import { getCollectionType, getPaymentType } from "../helpers";
import { PaymentAttemptsModal } from "../modals/PaymentAttemptsModal";
import { CollectedData } from "../types";

const collectedColumns: DataTableColumnDef<CollectedData>[] = [
  {
    header: "Booking Date",
    accessorKey: "bookingDate",
    size: 100,
    enableSorting: false,
  },
  {
    header: "Amount Collected",
    cell: (props) => props.row.original.amountCollected,
    size: 120,
    enableSorting: false,
  },
  {
    header: "Collection Type",
    accessorKey: "collectionType",
    size: 150,
    enableSorting: false,
  },
  {
    header: "Payment Type",
    accessorKey: "methodType",
    size: 220,
    enableSorting: false,
  },
  {
    header: "Status",
    cell: (props) => props.row.original.status,
    enableSorting: false,
  },
  {
    header: "Action",
    cell: (props) => props.row.original.action,
    size: 80,
    enableSorting: false,
  },
];

export const CollectedTable = ({ logs }: { logs: BookingPaymentLog[] }) => {
  const dispatch = useAppDispatch();
  const [selectedPaymentLog, setSelectedPaymentLog] = useState<BookingPaymentLog | null>(null);
  const [isPaymentAttemptsModalOpen, setIsPaymentAttemptsModalOpen] = useState(false);
  const { dateFormat } = useProfile();
  const {
    cost: { currency },
  } = useSelectedBooking();

  const data: CollectedData[] = [...logs]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .map((log) => ({
      id: log.id,
      bookingDate: format(new Date(log.createdAt), dateFormat),
      amountCollected: formatCurrency(log.amount, 2, currency),
      collectionType: getCollectionType(log.type, log.description),
      methodType: getPaymentType(log.type),
      status: <PaymentStatus status={log.status} />,
      action: (
        <DropdownMenu
          button={<Icon name="options" isCustom size="lg" />}
          position="bottom-start"
          className="!flex max-w-[24px]"
          offset={-4}
        >
          <DropdownMenu.Item
            onClick={() => {
              setSelectedPaymentLog(log);
              setIsPaymentAttemptsModalOpen(true);
            }}
          >
            <Icon name="DocumentText" size="sm" className="mr-2.5" />
            <Typography variant="paragraph">Payment Attempt Logs</Typography>
          </DropdownMenu.Item>
          {log.isRefundable && log?.balance && (
            <DropdownMenu.Item onClick={() => dispatch(setPaymentAction({ action: "process-refund", log }))}>
              <Icon name="Refresh2" size="sm" className="mr-2.5" />
              <Typography variant="paragraph">Refund</Typography>
            </DropdownMenu.Item>
          )}
        </DropdownMenu>
      ),
    }));

  return (
    <>
      <DataTable data={data} columns={collectedColumns} className="mt-5 w-full overflow-x-auto [&_table]:table-auto" />
      {selectedPaymentLog && (
        <PaymentAttemptsModal
          open={isPaymentAttemptsModalOpen}
          onClose={() => {
            setIsPaymentAttemptsModalOpen(false);
            setSelectedPaymentLog(null);
          }}
          selectedPaymentLog={selectedPaymentLog}
        />
      )}
    </>
  );
};
